import { computed, ref, type ComputedRef, type Ref } from "vue";

interface UseSegmentOptions {
  value: () => number | null;
  min: number;
  max: number;
  digits: number;
  disabled: () => boolean;
}

interface UseSegmentReturn {
  buffer: Ref<string>;
  displayValue: ComputedRef<string>;
  placeholder: string;
  resetBuffer: () => void;
  handleDigitInput: (digit: string) => { value: number; advance: boolean };
  handleBackspace: () => { value: number | null; navigatePrev: boolean };
}

export const useSegment = (options: UseSegmentOptions): UseSegmentReturn => {
  const { value, max, digits } = options;

  const buffer = ref("");

  const placeholder = "–".repeat(digits);

  const displayValue = computed(() => {
    const v = value();
    if (v === null) {
      return placeholder;
    }
    return v.toString().padStart(digits, "0");
  });

  const resetBuffer = (): void => {
    buffer.value = "";
  };

  const handleDigitInput = (
    digit: string
  ): { value: number; advance: boolean } => {
    const newBuffer = buffer.value + digit;
    let num = parseInt(newBuffer, 10);

    if (num > max) {
      num = parseInt(digit, 10);
      buffer.value = digit;
    } else {
      buffer.value = newBuffer;
    }

    const advance = num * 10 > max || buffer.value.length >= digits;

    if (advance) {
      buffer.value = "";
    }

    return { value: num, advance };
  };

  const handleBackspace = (): {
    value: number | null;
    navigatePrev: boolean;
  } => {
    const v = value();

    if (v === null) {
      return { value: null, navigatePrev: true };
    }

    const displayedStr = v.toString();
    const newStr = displayedStr.slice(0, -1);

    if (newStr.length === 0) {
      buffer.value = "";
      return { value: null, navigatePrev: false };
    }

    const num = parseInt(newStr, 10);
    buffer.value = newStr;
    return { value: num, navigatePrev: false };
  };

  return {
    buffer,
    displayValue,
    placeholder,
    resetBuffer,
    handleDigitInput,
    handleBackspace,
  };
};
